import { useState, useEffect, useCallback, useMemo } from "react";
import {
  ChartData,
  ChartDataParams,
  FormatChartData,
} from "../../../components/recharts/functions";
import {
  ReMixChart,
  lightenColor,
} from "../../../components/recharts/ReMixCharts";
import { BrushStart, ChartType, LegendKey } from "../../../models/chart_model";
import {
  CompareRajasthan,
  RajasthanCollective,
  RajBankingData,
} from "../../../models/rajasthan";
import { ExchangeColors } from "../../Dashboard/Exchange3/FormatData";
import { convertToString } from "../../../extensions/date";
import { ChartAreaLoader } from "../../../components/Loading";
import FilterMenu from "../../../components/FilterMenu";
import { R_CHART_TYPES } from "../const";
import { getProcurementData } from "./Data";
import { renderHourTick } from "../../../components/recharts/components";
import Tooltip from "../../../components/tooltip";
import MainPageContainer from "../../../components/MainPageContainer";
import NewFilterContainer from "../../../components/NewFilterContainer ";
import { useSelector } from "react-redux";
import SecondaryHeader from "../Demand/SecondaryHeader";
import LegendSummaryTable, {
  ChartTable,
  downloadChartTableCSV,
  downloadSummaryCSV,
} from "../../../components/table/SummaryTable";
import { SearchBox } from "../../../components/Search";
import { stringToConsistentColor } from "../../../extensions/string";
import { AddDatesWithFileName } from "../../../helpers/helper";
import BlockRangeSelector from "../../../components/BlockRangeSelector/BlockRangeSelector";
import { useLegendFilter } from "../../../hooks/useLegendFilter";

const KEYS = [
  {
    label: "Time Slot",
    key: "time",
    name: "Time Slot",
    firstKey: "time_block",
    secondKey: "date",
    values: ["time_block", "date"],
    isTime: true,
  },
  {
    label: "Date",
    key: "date",
    name: "Date",
    firstKey: "date",
    secondKey: undefined,
    values: ["date"],
    isTime: false,
  },
  {
    label: "Month",
    key: "month",
    name: "Month",
    firstKey: "month",
    secondKey: undefined,
    values: ["month"],
    isTime: false,
  },
];

interface RajData {
  collective: RajasthanCollective[];
  compare: CompareRajasthan[];
  bankingData: RajBankingData[];
}

const UNITS = ["MW", "MWh"];

const GROUP_BY = [
  {
    label: "Product",
    key: "product",
  },
  {
    label: "Exchange",
    key: "exchange",
  },
];

export default function ProcurementOverview() {
  const dataTab = useSelector((state: any) => state.viewMode.activeView);
  const activeTab = useSelector((state: any) => state.switchTabs.activeTab);
  const [uniqueBlock, setUniqueBlock] = useState<any[]>([]);
  const [selectedBlock, setSelectedBlock] = useState<any[]>([]);
  const DefaultDates = useSelector(
    (state: any) => state.defaultDatesSetter.defaultDates
  );
  const [TableData, setTableData] = useState<any[]>([]);
  const [chartData, setChartData] = useState<ChartData>();
  const [selectedKey, setSelectedKey] = useState(KEYS[0]);
  const [selectedGroupKeys, setSelectedGroupKeys] = useState([
    GROUP_BY[0],
    GROUP_BY[1],
  ]);
  const [selectedEntity, setSelectedEntity] = useState<string[]>([]);
  const [selectedUnit, setSelectedUnit] = useState(UNITS[0]);

  const [loading, setLoading] = useState(true);
  const [selectedChartType, setSelectedChartType] = useState(R_CHART_TYPES[1]);

  const [data, setData] = useState<RajData>({
    collective: [],
    compare: [],

    bankingData: [],
  });

  const uniqueEntities = Array.from(
    new Set(data.compare.map((item) => [item.entity, item.toEntity]).flat())
  ).filter((item) => item !== undefined) as string[];

  const _handlefetchData = useCallback(async () => {
    if (DefaultDates[activeTab].defaultRangeDate === undefined) {
      return;
    }
    setLoading(true);
    const dates = DefaultDates[activeTab].defaultRangeDate;
    const startDateStr = convertToString(dates.startDate);
    const endDateStr = convertToString(dates.endDate);

    const res = await getProcurementData({
      startDate: startDateStr,
      endDate: endDateStr,
    });
    setUniqueBlock(
      Array.from(new Set(res.data.compare.map((item) => item.time_block)))
    );
    setData(res.data);
    setLoading(false);
  }, [activeTab, DefaultDates]);

  useEffect(() => {
    _handlefetchData();
  }, [_handlefetchData]);

  useEffect(() => {
    let filteredData = data.compare;
    if (selectedEntity.length > 0) {
      filteredData = filteredData.filter(
        (item) =>
          selectedEntity.includes(item.entity!) ||
          selectedEntity.includes(item.toEntity!)
      );
    }

    if (selectedBlock.length > 0) {
      filteredData = filteredData.filter((item) =>
        selectedBlock.map((item) => item.value).includes(item.time_block)
      );
    }

    function getKeys() {
      if (selectedKey.key === "time") {
        return ["date", "time_block"];
      } else if (selectedKey.key === "date") {
        return ["date"];
      } else {
        return [selectedKey.values[0]];
      }
    }
    const par: ChartDataParams = {
      keys: getKeys(),
      tableKeys: ["time_block", "date", "month"],
      groupBy: selectedGroupKeys.map((item) => item.key),
      volumeKey: "value_mwh",
      aggregationKeys: ["value_mw"],
      priceKey: "price_kwh",
      averageKeys: ["value_mw"],
      averageDuplicationKeys: ["time_block", "date"],
      data: filteredData,
    };

    const res = FormatChartData(par);
    setTableData(res.tableData || []);
    setChartData(res as any);
  }, [data, selectedKey, selectedEntity, selectedGroupKeys, selectedBlock]);

  const handleKeyChange = (index: number) => {
    setSelectedKey(KEYS[index]);
  };

  const handlexDataKey = useCallback(() => {
    if (selectedKey.key === "time") {
      if (selectedBlock.length > 0) {
        return "time_block";
      }
      return "time_block";
    } else {
      // if (selectedUnit !== DEMAND_UNITS[1]) {
      //   setSelectedUnit(DEMAND_UNITS[1]);
      // }
      return selectedKey.key;
    }
  }, [selectedBlock.length, selectedKey.key]);

  const xDataKey = useMemo(() => handlexDataKey(), [handlexDataKey]);

  const summaryExtraKeys = selectedKey.isTime
    ? [
        "time_block", // Changed dateIndex to date
        selectedKey.values[1],
      ]
    : [selectedKey.values[0]];

  const summaryExtraLabels = selectedKey.values.map(
    (value) => (value === "date" ? "Date" : value) // Simplified label mapping
  );

  const allLegends = useMemo(
    () => getActiveLegends(),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [chartData?.keys, selectedUnit, selectedChartType]
  );

  const { visibleLegends, handleLegendSelect, selectedLegends: selectedLegendKeys } =
    useLegendFilter(allLegends as any);

  const tablePar = {
    legends: visibleLegends,
    data: chartData?.data || [],
    itemsPerPage: 96,
    tableData: TableData,
    unit: selectedUnit,
    extraKeys: summaryExtraKeys,
    extraLabels: summaryExtraLabels,
    filename: AddDatesWithFileName({
      fileName: "Power Procurement Overview",
      dates: DefaultDates[activeTab].defaultRangeDate,
    }),
  };

  function secondDataKey() {
    if (selectedKey.isTime) {
      if (selectedBlock.length > 0) {
        return "date";
      }
      return "date";
    } else {
      return undefined;
    }
  }

  return (
    <div className="gas-page-container">
      <NewFilterContainer>
        <div className="side-filters-container text-nowrap">
          <FilterMenu
            items={KEYS.map((item) => item.name)}
            selectedItems={[selectedKey.name]}
            onSelect={(item, index) => {
              handleKeyChange(index);
            }}
            title={"Interval"}
          />

          <FilterMenu
            items={GROUP_BY.map((item) => item.label)}
            selectedItems={selectedGroupKeys.map((item) => item.label)}
            setSelectedItems={(items) => {
              setSelectedGroupKeys(
                items.map((item) => {
                  return GROUP_BY.find((i) => i.label === item)!;
                })
              );
            }}
            title={"Group By"}
          />

          <FilterMenu
            items={UNITS}
            selectedItems={[selectedUnit]}
            onSelect={(item, index) => {
              setSelectedUnit(item);
            }}
            title={"Unit"}
          />

          <FilterMenu
            title="Chart Type"
            items={R_CHART_TYPES.map((item) => item.label)}
            selectedItems={[selectedChartType.label]}
            onSelect={(item, index) => {
              setSelectedChartType(R_CHART_TYPES[index]);
            }}
          />
        </div>
      </NewFilterContainer>
      <MainPageContainer>
        <SecondaryHeader />
        <div className="gas-chart-container">
          {loading ? <ChartAreaLoader title="Loading Procurement Overview" subtitle="Fetching short term power procurement data…" /> : (<>
          <div className="gas-heading mb-4">
            <h2 className="chartHeading">Short Term Procurement</h2>
            <div className="flex gap-x-6  justify-end">
              {" "}
              <BlockRangeSelector
                blocks={Array.from({ length: 96 }, (_, i) => i + 1)}
                placeholder="Search Block"
                selectedOptions={selectedBlock.map((item) => item.value)}
                setSelectedOptions={setSelectedBlock}
              />
              <button
                onClick={() => {
                  dataTab.label === "Chart" && downloadChartTableCSV(tablePar);
                  dataTab.label === "Summary" && downloadSummaryCSV(tablePar);
                  dataTab.label === "Table" && downloadChartTableCSV(tablePar);
                }}
                className="downloadButton"
              >
                Download Data
              </button>
            </div>
          </div>
          {dataTab.label === "Chart" && (
            <ReMixChart
              data={chartData?.data || []}
              xDataKey={xDataKey}
              xTick={
                selectedKey.isTime && selectedBlock.length == 0
                  ? (value: any, index: any) => {
                      return renderHourTick(value, 10);
                    }
                  : undefined
              }
              secondXDataKey={secondDataKey()}
              brushStart={BrushStart.Start}
              isTimeSlot={selectedKey.isTime}
              onlyTitle={true}
              secondYAxisLabel="Rs/kWh"
              barCategoryGap={12}
              yAxisWidth={60}
              secondYAxisWidth={35}
              yAxisLabel={selectedUnit}
              showBrush={getShowBrush(chartData?.data || [])}
              legends={allLegends}
              onLegendSelect={handleLegendSelect}
              defaultSelectedLegends={selectedLegendKeys}
            />
          )}
          {dataTab.label === "Summary" && <LegendSummaryTable {...tablePar} />}
          {dataTab.label === "Table" && <ChartTable {...tablePar} />}
          </>)}
        </div>
        <div className="gna-footer-container">
          <div className="grid grid-cols-3 justify-between gap-5 ">
            <Tooltip
              note={<div> RUVITL, WBES</div>}
              text="1. RUVITL: DEEP contracts, Banking
                Agreements, LDC contracts
                2. WBES: OA_PX reports, OA GNA reports, OA TGNA reports
                3. MW = Represents the average (MW) when the selected time interval is Date.
"
            >
              <p className="gna-footer-text">Source</p>
            </Tooltip>
            <p className="gna-footer-text col-span-1 text-center">
              Powered by <strong>GNA Energy</strong>
            </p>
            <strong className="gna-footer-text col-span-1 text-end">
              Beta Version
            </strong>
          </div>
        </div>
      </MainPageContainer>
    </div>
  );
  function getActiveLegends() {
    const keys =
      (chartData?.keys
        .filter((i) => !i.includes("undefined"))
        .map((key, index) => {
          return {
            dataKey: key + "_value_" + selectedUnit.toLowerCase(),
            name: key
              .replaceAll("_", " ")
              .replace("null", "")
              .replace("Banking Banking", "Banking"),
            stackId: selectedChartType.stackId,
            // legendColor: undefined,
            // stroke: stringToConsistentColor(key),
            type: selectedChartType.value,
            unit: selectedUnit,
          };
        }) as LegendKey[]) || [];

    const pricingKeys =
      (chartData?.keys
        .filter((i) => !i.includes("undefined"))
        .map((key, index) => {
          return {
            dataKey: key + "_price_kwh",
            name:
              key
                .replaceAll("_", " ")
                .replace("null", "")
                .replace("Banking Banking", "Banking") + " Price",
            stackId: "generation",
            unit: "Rs/kWh",
            // stroke: lightenColor(ExchangeColors[index], 10),
            type: ChartType.Line,
            yAxisId: "right",
          };
        }) as LegendKey[]) || [];

    return keys.concat(pricingKeys);
  }

  function getShowBrush(chartData: any[]): boolean {
    if (selectedKey.firstKey === "time_block") {
      return chartData.length > 96 * 1;
    } else {
      return chartData.length > 8;
    }
  }
}
